import type { Language } from '../../i18n/translations.js';
import { DEMO_COPY } from './demo-copy.js';
import type { DragEvent } from 'react';
import { DEMO_EXAMPLES, type DemoExample } from './demo-media.js';
export function ProductImageStage({
  example,
  language,
  step,
  go,
  selected,
  dragging,
  setSelected,
  setDragging,
  startDrag,
  dragOver,
  dropProduct,
}: {
  readonly example: DemoExample;
  readonly language: Language;
  readonly step: number;
  readonly go: (next: number) => void;
  readonly selected: boolean;
  readonly dragging: boolean;
  readonly setSelected: (value: boolean) => void;
  readonly setDragging: (value: boolean) => void;
  readonly startDrag: (event: DragEvent<HTMLButtonElement>) => void;
  readonly dragOver: (event: DragEvent<HTMLDivElement>) => void;
  readonly dropProduct: (event: DragEvent<HTMLDivElement>) => void;
}) {
  const c = DEMO_COPY[language];
  return (
    <section
      className="stage mw-stage mw-product-stage"
      id="stage-3"
      aria-labelledby="product-title"
      hidden={step !== 3}
    >
      <div className="mw-stage-heading">
        <h3 id="product-title" tabIndex={-1}>
          {c.addProductImages}
        </h3>
      </div>
      <div className="product-layout">
        <figure className="selected-video">
          <img
            src={DEMO_EXAMPLES[example].sourcePoster}
            alt={
              example === 'croissant'
                ? c.selectedCroissantVideo
                : c.theSelectedVideoShowsAPerson
            }
            width="540"
            height="960"
            loading="lazy"
          />
          <figcaption>{c.yourChosenVideo}</figcaption>
        </figure>
        <div className="product-content">
          <div className="mw-guide mw-guide-above" id="product-guide">
            <span className="mw-guide-dot" aria-hidden="true"></span>
            <p>{c.tapOrDragTheImage}</p>
          </div>
          <div
            className="product-grid"
            role="group"
            aria-label={c.preparedProductImages}
          >
            <button
              className="product"
              id="select-cookie"
              aria-describedby="product-guide"
              type="button"
              draggable={true}
              onClick={() => {
                setSelected(true);
              }}
              onDragStart={startDrag}
              onDragEnd={() => {
                setDragging(false);
              }}
              aria-pressed={selected}
            >
              <span className="selection-check" aria-hidden="true">
                {'✓'}
              </span>
              <img
                src="/landing-demo/cookie.png"
                alt=""
                draggable={false}
                width="160"
                height="160"
              />
              <strong>{c.dropSelected}</strong> <small>{c.tryThisOne}</small>
            </button>
            <div className="product preview-product" aria-disabled="true">
              <svg viewBox="0 0 120 120" fill="none" aria-hidden="true">
                <rect x="44" y="15" width="32" height="17" rx="2" fill="#263340"></rect>
                <rect
                  x="31"
                  y="34"
                  width="58"
                  height="74"
                  rx="5"
                  fill="#e7c5a8"
                  stroke="#c6a287"
                ></rect>
                <rect x="38" y="55" width="44" height="31" fill="#fffaf1"></rect>
                <path d="M48 66h24M52 73h16" stroke="#a58872"></path>
              </svg>
              <strong>{c.signatureScent}</strong>
              <small>{c.previewOnly2}</small>
            </div>
            <div className="product preview-product" aria-disabled="true">
              <svg viewBox="0 0 120 120" fill="none" aria-hidden="true">
                <path d="M34 37h52l-6 68H40z" fill="#c5d0bc" stroke="#98a88d"></path>
                <rect x="30" y="28" width="60" height="12" rx="3" fill="#2b423a"></rect>
                <path d="M37 27l3-8h40l3 8" fill="#415b4d"></path>
                <rect x="38" y="57" width="44" height="27" fill="#f3efe6"></rect>
                <path d="M49 66h22M53 73h14" stroke="#8e9c80"></path>
              </svg>
              <strong>{c.dailyCoffee}</strong>
              <small>{c.previewOnly2}</small>
            </div>
          </div>
          <div
            id="dropzone"
            className={`dropzone${selected ? ' filled' : ''}${dragging ? ' dragover' : ''}`}
            onDragOver={dragOver}
            onDragLeave={() => {
              setDragging(false);
            }}
            onDrop={dropProduct}
          >
            <span
              className="drop-plus"
              id="drop-plus"
              aria-hidden="true"
              hidden={selected}
            >
              {'＋'}
            </span>
            <img
              id="drop-image"
              src="/landing-demo/cookie.png"
              alt=""
              width="58"
              height="58"
              hidden={!selected}
            />
            <div>
              <strong id="drop-label">{selected ? c.dropSelected : c.dropEmpty}</strong>
              <p id="drop-help" className="sr">
                {selected
                  ? example === 'croissant'
                    ? c.croissantDropHelp
                    : c.dropHelpSelected
                  : c.dropHelpEmpty}
              </p>
            </div>
          </div>
          <button
            className="text-btn mw-remove"
            id="remove-product"
            type="button"
            hidden={!selected}
            onClick={() => {
              setSelected(false);
            }}
          >
            {c.removeProduct}
          </button>
          <div className="product-actions">
            <span className="selection-status sr" id="selection-status" role="status">
              {selected ? c.selectionReady : c.selectionEmpty}
            </span>
            <button
              className="btn"
              id="show-result"
              type="button"
              disabled={!selected}
              onClick={() => {
                go(4);
              }}
            >
              <span className="cs-px3" aria-hidden="true">
                <i></i>
                <i></i>
                <i></i>
              </span>
              <span>{c.showTheResult}</span>
              <span className="arrow" aria-hidden="true">
                {'→'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
